import Papa from "papaparse";
import type { FormBuilderForm } from "../../../generated/nuggetKmsApi/graphql";
import type { GetSubmissionsQuery } from "../../../generated/nuggetKmsApi/graphql";

/** Chunk size for paginated submission fetch when exporting. */
export const EXPORT_CHUNK_SIZE = 1000;

export interface ExportColumnDef {
	itemFieldId: number;
	name: string;
}

/**
 * Builds ordered column definitions from getFormBuilderForm response.
 * Uses API order: mapFormBuilderForm (formBuilderResolvers) returns sections,
 * items, and itemFields already sorted by sortOrder. No client-side sorting.
 */
export const buildExportColumnsFromForm = (form: FormBuilderForm): ExportColumnDef[] => {
	const columns: ExportColumnDef[] = [];
	for (const section of form.sections ?? []) {
		for (const item of section.items ?? []) {
			for (const field of item.itemFields ?? []) {
				columns.push({
					itemFieldId: field.id,
					name: field.name ?? field.code ?? `Question ${field.id}`,
				});
			}
		}
	}
	return columns;
};

export type SubmissionRow = GetSubmissionsQuery["getSubmissions"]["rows"][number];

/**
 * Builds CSV string using papaparse.
 * Columns: Submission ID, then one per form question (by sortOrder).
 */
export const buildCsvFromSubmissions = (
	columns: ExportColumnDef[],
	submissions: SubmissionRow[],
): string => {
	const fields = ["Submission ID", ...columns.map((c) => c.name)];

	const data = submissions.map((submission) => {
		const valueMap = new Map<number, string>();
		for (const sif of submission.submissionItemFields) {
			valueMap.set(sif.itemFieldId, sif.value ?? "");
		}
		return [submission.id, ...columns.map((c) => valueMap.get(c.itemFieldId) ?? "")];
	});

	return Papa.unparse({ fields, data });
};

/**
 * Triggers browser download of CSV file.
 */
export const triggerCsvDownload = (csv: string, filename: string): void => {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};
